import React, { useState } from "react";
import { X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const TYPE_TITLES = {
  demo: "Request a Demo",
  trial: "Start Your Free Trial",
  subscribe: "Subscribe / Inquire",
  onprem: "Get an On-Premise Proposal",
  hardware: "Hardware Solutions Inquiry",
  software: "Software Development Inquiry",
  infrastructure: "Infrastructure Solutions Inquiry",
  custom: "Custom System Inquiry",
  contact: "Contact MSR",
};

export default function RFQModal({ inquiryType, solutionSlug = "", solutionName = "", onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    position: "",
    industry: "",
    budget_range: "",
    timeline: "",
    requirements: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", {
        inquiry_type: inquiryType,
        solution_slug: solutionSlug,
        ...form,
      });
      setDone(true);
      toast.success("Inquiry submitted. Our team will reach out within 3 working days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" data-testid="rfq-modal">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto relative border-t-4 border-msr-blue">
        <button onClick={onClose} className="absolute top-4 right-4 text-msr-slate hover:text-msr-ink" data-testid="rfq-modal-close-btn">
          <X size={22} />
        </button>
        <div className="p-6 lg:p-10">
          {done ? (
            <div className="text-center py-10" data-testid="rfq-modal-success">
              <div className="overline text-msr-green mb-3">Thank you</div>
              <h2 className="font-heading font-extrabold text-2xl mb-3">Your inquiry has been received.</h2>
              <p className="text-msr-slate">Our team is reviewing your requirements. Expect a response within 3 working days.</p>
              <button onClick={onClose} className="mt-8 btn-msr-primary">Close</button>
            </div>
          ) : (
            <>
              <div className="overline mb-2">{solutionName || "MSR Business Solutions"}</div>
              <h2 className="font-heading font-extrabold text-2xl lg:text-3xl tracking-tight mb-6">{TYPE_TITLES[inquiryType] || "Inquire"}</h2>
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full Name *" testId="rfq-name" value={form.name} onChange={(v) => update("name", v)} required />
                <Field label="Email *" type="email" testId="rfq-email" value={form.email} onChange={(v) => update("email", v)} required />
                <Field label="Phone" testId="rfq-phone" value={form.phone} onChange={(v) => update("phone", v)} />
                <Field label="Company" testId="rfq-company" value={form.company} onChange={(v) => update("company", v)} />
                <Field label="Position / Title" testId="rfq-position" value={form.position} onChange={(v) => update("position", v)} />
                <Field label="Industry / Business Type" testId="rfq-industry" value={form.industry} onChange={(v) => update("industry", v)} />
                <Field label="Budget Range" testId="rfq-budget" value={form.budget_range} onChange={(v) => update("budget_range", v)} />
                <Field label="Timeline" testId="rfq-timeline" value={form.timeline} onChange={(v) => update("timeline", v)} placeholder="e.g. Within 30 days" />
                <div className="sm:col-span-2">
                  <TextArea label="Requirements" testId="rfq-requirements" value={form.requirements} onChange={(v) => update("requirements", v)} rows={3} />
                </div>
                <div className="sm:col-span-2">
                  <TextArea label="Additional Notes" testId="rfq-notes" value={form.notes} onChange={(v) => update("notes", v)} rows={2} />
                </div>
                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
                  <button type="submit" disabled={submitting} className="btn-msr-primary disabled:opacity-60" data-testid="rfq-submit-btn">
                    {submitting ? "Submitting..." : "Submit Inquiry"}
                  </button>
                  <span className="text-xs text-msr-slate">Standard turnaround: 3 working days.</span>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <input
        type={type}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full border border-msr-line px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue"
        data-testid={testId}
      />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full border border-msr-line px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue resize-none"
        data-testid={testId}
      />
    </label>
  );
}
